import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles/tokens.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ToastProvider, ToastViewport } from "@/components/ui/toast";
import { AppShell } from "@/components/AppShell";
import LoginPage from "@/routes/LoginPage";
import BrowsePage from "@/routes/BrowsePage";
import AdminUsersPage from "@/routes/AdminUsersPage";
import AdminPercentagesPage from "@/routes/AdminPercentagesPage";
import AdminCatalogPage from "@/routes/AdminCatalogPage";
import AdminUploadPage from "@/routes/AdminUploadPage";
import AdminAuditPage from "@/routes/AdminAuditPage";
import PrintPage from "@/routes/PrintPage";

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

function RequireAuth({ children, admin = false, users = false }: { children: React.ReactNode; admin?: boolean; users?: boolean }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // 👤 ผู้ใช้ also opens for an SQ2.0 manager (usersAdmin from /api/me)
  if (users ? !(user.role === "admin" || user.usersAdmin) : admin && user.role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Root() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/print/:tier" element={<RequireAuth><PrintPage /></RequireAuth>} />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/" element={<BrowsePage />} />
        <Route path="/admin/users" element={<RequireAuth users><AdminUsersPage /></RequireAuth>} />
      <Route path="/account" element={<RequireAuth><AdminUsersPage /></RequireAuth>} />
        <Route path="/admin/percentages" element={<RequireAuth admin><AdminPercentagesPage /></RequireAuth>} />
        <Route path="/admin/catalog" element={<RequireAuth admin><AdminCatalogPage /></RequireAuth>} />
        <Route path="/admin/upload" element={<RequireAuth admin><AdminUploadPage /></RequireAuth>} />
        <Route path="/admin/audit" element={<RequireAuth admin><AdminAuditPage /></RequireAuth>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Root />
          </BrowserRouter>
        </AuthProvider>
        <ToastViewport />
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
